import { useState, useEffect, useCallback } from 'react';
import MobileLayout from '../../components/MobileLayout';
import './MobileBills.css';

const PAGE_SIZE = 20;

// Dot accent colors for list rows (visual only, not used on chips)
const DOT_COLOR_CYCLE = ['#ea580c', '#7c3aed', '#2563eb', '#16a34a', '#0891b2', '#db2777', '#ca8a04'];
const dotColorCache = {};
let dotColorIdx = 0;
const getDotColor = (type) => {
  if (!dotColorCache[type]) {
    dotColorCache[type] = DOT_COLOR_CYCLE[dotColorIdx % DOT_COLOR_CYCLE.length];
    dotColorIdx++;
  }
  return dotColorCache[type];
};

const formatDate = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
};

const formatDateShort = (s) => {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
};

const normalizeEvent = (item, idx) => ({
  id:         item.id         || `ev-${idx}`,
  chargeType: item.chargeType || item.charge_type || '未知类型',
  amount:     Number(item.amount) || 0,
  createdAt:  item.createdAt  || item.created_at  || '',
});

const normalizeBenefit = (item, idx) => ({
  id:        `ben-${idx}`,
  name:      item.name     || '套餐',
  total:     Number(item.total) || 0,
  used:      Number(item.used)  || 0,
  startedAt: item.startedAt    || item.started_at || '',
  endAt:     item.endAt        || item.end_at     || '',
});

export default function MobileBills() {
  const [tab, setTab]               = useState('consume');
  const [items, setItems]           = useState([]);
  const [allItems, setAllItems]     = useState([]); // full list for deriving types
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [page, setPage]             = useState(1);
  const [selectedType, setSelectedType] = useState('all');

  // Credits summary
  const [credits, setCredits] = useState(null);

  useEffect(() => {
    fetch('/api/billing/benefits', { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => {
        if (data.code === 0 && data.data) {
          const total = Number(data.data.total)     || 0;
          const used  = Number(data.data.totalUsed) || 0;
          setCredits({ total, used, remaining: total - used });
        }
      })
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async (nextPage = 1, reset = true) => {
    setLoading(true);
    setError('');
    try {
      if (tab === 'consume') {
        const res = await fetch(
          `/api/billing/benefit_events?page=${nextPage}&pageSize=${PAGE_SIZE}`,
          { credentials: 'include' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.code !== undefined && data.code !== 0) throw new Error(data.msg || `错误 ${data.code}`);
        const d = data?.data ?? data ?? {};
        const newItems = (d.items ?? []).map(normalizeEvent);
        setTotalItems(Number(d.total ?? newItems.length));
        // 使用函数式更新避免闭包陷阱
        setItems(prev => reset ? newItems : [...prev, ...newItems]);
        setAllItems(prev => reset ? newItems : [...prev, ...newItems]);
      } else {
        const res = await fetch('/api/billing/benefits', { credentials: 'include' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (data.code !== undefined && data.code !== 0) throw new Error(data.msg || `错误 ${data.code}`);
        const d = data?.data ?? data ?? {};
        const newItems = (d.items ?? [])
          .filter((item) => Number(item.total) > 0)
          .map(normalizeBenefit);
        setItems(newItems);
        setAllItems(newItems);
        setTotalItems(newItems.length);
      }
    } catch (err) {
      setError(err.message || '加载失败');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  // Reset and fetch when tab changes
  useEffect(() => {
    setPage(1);
    setSelectedType('all');
    setAllItems([]);
    fetchData(1, true);
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLoadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchData(next, false);
  };

  // Unique charge types derived from all loaded data (for filter chips)
  const chargeTypes = [...new Set(allItems.map((i) => i.chargeType).filter(Boolean))];

  // Client-side type filter for consume tab
  const displayItems = tab === 'consume' && selectedType !== 'all'
    ? items.filter((i) => i.chargeType === selectedType)
    : items;

  const totalPages = Math.ceil(totalItems / PAGE_SIZE) || 1;
  const hasMore = tab === 'consume' && page < totalPages;

  const fmt = (v) => new Intl.NumberFormat('zh-CN').format(Number.isFinite(v) ? v : 0);
  const pct = credits && credits.total > 0
    ? Math.min(100, Math.round((credits.remaining / credits.total) * 100))
    : 0;

  return (
    <MobileLayout title="积分明细" showBack>
      <div className="mbills-root">

        {/* ── Balance summary card ── */}
        <div className="mbills-summary">
          {/* Row 1: big remaining + pct tag */}
          <div className="mbills-summary-top">
            <span className="mbills-summary-num">{credits ? fmt(credits.remaining) : '—'}</span>
            <span className="mbills-summary-pct-tag">{credits ? `可用 ${pct}%` : '—'}</span>
          </div>
          {/* Row 2: fraction label */}
          <div className="mbills-summary-fraction">
            <span className="mbills-summary-slash">/ </span>
            <span className="mbills-summary-total">{credits ? fmt(credits.total) : '—'}</span>
            <span className="mbills-summary-unit"> 积分</span>
          </div>
          <div className="mbills-summary-bar-track">
            <div className="mbills-summary-bar-fill" style={{ width: `${pct}%` }} />
          </div>
          <div className="mbills-summary-stats">
            <span className="mbills-summary-stat">
              <span className="mbills-summary-stat-val">{credits ? fmt(credits.remaining) : '—'}</span>
              <span className="mbills-summary-stat-key">可用</span>
            </span>
            <span className="mbills-summary-stat-divider" />
            <span className="mbills-summary-stat">
              <span className="mbills-summary-stat-val">{credits ? fmt(credits.used) : '—'}</span>
              <span className="mbills-summary-stat-key">已消耗</span>
            </span>
            <span className="mbills-summary-stat-divider" />
            <span className="mbills-summary-stat">
              <span className="mbills-summary-stat-val">{credits ? fmt(credits.total) : '—'}</span>
              <span className="mbills-summary-stat-key">总额度</span>
            </span>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="mbills-tabs">
          <button
            className={`mbills-tab${tab === 'consume' ? ' active' : ''}`}
            onClick={() => setTab('consume')}
          >消耗记录</button>
          <button
            className={`mbills-tab${tab === 'gain' ? ' active' : ''}`}
            onClick={() => setTab('gain')}
          >套餐列表</button>
        </div>

        {/* ── Consume: type filter chips (dynamic from real data) ── */}
        {tab === 'consume' && chargeTypes.length > 0 && (
          <div className="mbills-chips-wrap">
            <div className="mbills-chips">
              <button
                className={`mbills-chip${selectedType === 'all' ? ' active' : ''}`}
                onClick={() => setSelectedType('all')}
              >全部</button>
              {chargeTypes.map((type) => (
                <button
                  key={type}
                  className={`mbills-chip${selectedType === type ? ' active' : ''}`}
                  onClick={() => setSelectedType(type)}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Content ── */}
        {loading && items.length === 0 ? (
          <div className="mbills-loading"><div className="mbills-spinner" /></div>
        ) : error ? (
          <div className="mbills-error">
            {error}
            <button className="mbills-retry" onClick={() => fetchData(1, true)}>重试</button>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="mbills-empty">暂无记录</div>
        ) : tab === 'consume' ? (
          <div className="mbills-list">
            {displayItems.map((item) => {
              const color = getDotColor(item.chargeType);
              return (
                <div className="mbills-row" key={item.id} style={{ '--accent': color }}>
                  <div className="mbills-row-dot" />
                  <div className="mbills-row-body">
                    <div className="mbills-row-type">{item.chargeType}</div>
                    <div className="mbills-row-date">{formatDate(item.createdAt)}</div>
                  </div>
                  <div className="mbills-row-amount">−{fmt(item.amount)}</div>
                </div>
              );
            })}
            {/* Load more */}
            {hasMore && (
              <button
                className="mbills-load-more"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? '加载中...' : '加载更多'}
              </button>
            )}
          </div>
        ) : (
          /* Gain tab: package cards */
          <div className="mbills-pkg-list">
            {items.map((item) => {
              const usedPct = item.total > 0
                ? Math.min(100, Math.round((item.used / item.total) * 100))
                : 0;
              return (
                <div className="mbills-pkg-card" key={item.id}>
                  <div className="mbills-pkg-header">
                    <div className="mbills-pkg-name">{item.name}</div>
                    <div className="mbills-pkg-fraction">
                      <span className="mbills-pkg-used">{fmt(item.used)}</span>
                      <span className="mbills-pkg-sep"> / </span>
                      <span className="mbills-pkg-total">{fmt(item.total)}</span>
                      <span className="mbills-pkg-unit"> 积分</span>
                    </div>
                  </div>
                  <div className="mbills-pkg-bar-track">
                    <div className="mbills-pkg-bar-fill" style={{ width: `${usedPct}%` }} />
                  </div>
                  <div className="mbills-pkg-meta">
                    <span>已用 {usedPct}%</span>
                    {item.startedAt && (
                      <span>{formatDateShort(item.startedAt)} ~ {formatDateShort(item.endAt)}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>
    </MobileLayout>
  );
}
